import React, { useState, useEffect } from 'react';
import { 
  CalendarIcon, 
  ClockIcon, 
  MapPinIcon, 
  UserGroupIcon, 
  CheckIcon,
  XMarkIcon,
  PhoneIcon,
  UserIcon,
  InformationCircleIcon,
  BuildingLibraryIcon,
  HomeIcon,
  FunnelIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';
import axiosInstance from '../../utils/axios';
import API_URLS from '../../config/api';
import { formatDate } from '../../utils/dateUtils';

function RideRequests() {
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedService, setSelectedService] = useState('');
  const [selectedDirection, setSelectedDirection] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [expandedRequestId, setExpandedRequestId] = useState(null);

  // Church address for comparison
  const churchAddress = "Unit 2, 3740 27 St NE, Calgary, Alberta T1Y 5E2";

  useEffect(() => {
    fetchRideRequests();
  }, [selectedDate, selectedService]);

  useEffect(() => {
    filterRequests();
  }, [requests, selectedDirection, selectedCity]);

  const fetchRideRequests = async () => {
    try {
      setLoading(true);
      let url = `${API_URLS.bookings.requests}`;
      const params = new URLSearchParams();
      
      if (selectedDate) params.append('date', selectedDate);
      if (selectedService) params.append('serviceSession', selectedService);
      
      const response = await axiosInstance.get(`${url}?${params.toString()}`);
      setRequests(response.data);
    } catch (err) {
      console.error('Error fetching ride requests:', err);
      setError(err.response?.data?.message || 'Failed to load ride requests');
    } finally {
      setLoading(false);
    }
  };

  const determineRideDirection = (request) => {
    // Check if destination contains church address or is generic "Church"
    const destination = request.destination || '';
    const isToChurch = destination.includes('3740 27 St NE') || 
                      destination.toLowerCase().includes('church') ||
                      destination === 'Church';
    
    return isToChurch ? 'to-church' : 'from-church';
  };

  const extractCity = (request) => {
    // Extract city from pickup or destination address
    const direction = determineRideDirection(request);
    
    if (direction === 'to-church') {
      // For "to church" rides, get city from pickup location
      return request.pickupCity || 
             (request.pickupLocation && request.pickupLocation.split(',')[1]?.trim().split(' ')[0]) || 
             'Unknown';
    } else {
      // For "from church" rides, get city from destination
      if (request.destinationCity) {
        return request.destinationCity;
      }
      // Try to extract from destination string
      const destinationParts = request.destination?.split(',') || [];
      if (destinationParts.length > 1) {
        return destinationParts[1].trim().split(' ')[0];
      }
      return 'Unknown';
    }
  };

  const filterRequests = () => {
    let filtered = [...requests];

    // Filter by direction
    if (selectedDirection) {
      filtered = filtered.filter(request => {
        const direction = determineRideDirection(request);
        return direction === selectedDirection;
      });
    }

    // Filter by city
    if (selectedCity) {
      filtered = filtered.filter(request => {
        const city = extractCity(request);
        return city.toLowerCase().includes(selectedCity.toLowerCase());
      });
    }

    setFilteredRequests(filtered);
  };

  // Get unique cities for filter dropdown
  const getUniqueCities = () => {
    const cities = requests.map(request => extractCity(request));
    return [...new Set(cities)].filter(city => city !== 'Unknown').sort();
  };

  const handleAcceptRequest = async (requestId) => {
    try {
      await axiosInstance.put(`${API_URLS.bookings.requests}/${requestId}/accept`);
      
      // Update the request status locally
      setRequests(requests.map(request => 
        request.id === requestId ? {...request, status: 'accepted'} : request
      ));
    } catch (err) {
      console.error('Error accepting ride request:', err);
      alert('Failed to accept ride request. Please try again.');
    }
  };

  const handleDeclineRequest = async (requestId) => {
    try {
      await axiosInstance.put(`${API_URLS.bookings.requests}/${requestId}/decline`);
      
      // Update the request status locally
      setRequests(requests.map(request => 
        request.id === requestId ? {...request, status: 'declined'} : request
      ));
    } catch (err) {
      console.error('Error declining ride request:', err);
      alert('Failed to decline ride request. Please try again.');
    }
  };

  const toggleExpandRequest = (requestId) => {
    if (expandedRequestId === requestId) {
      setExpandedRequestId(null);
    } else {
      setExpandedRequestId(requestId);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <ClockIcon className="animate-spin h-8 w-8 text-indigo-500" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Ride Requests</h1>
      
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      <div className="mb-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div>
          <label htmlFor="date" className="block text-sm font-medium text-gray-700">
            Date
          </label>
          <input
            type="date"
            id="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
          />
        </div>
        <div>
          <label htmlFor="service" className="block text-sm font-medium text-gray-700">
            Service
          </label>
          <select
            id="service"
            value={selectedService}
            onChange={(e) => setSelectedService(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
          >
            <option value="">All Services</option>
            <option value="1st">1st Service</option>
            <option value="2nd">2nd Service</option>
            <option value="3rd">3rd Service</option>
            <option value="midweek">Midweek</option>
          </select>
        </div>
        <div>
          <label htmlFor="direction" className="block text-sm font-medium text-gray-700">
            Direction
          </label>
          <select
            id="direction"
            value={selectedDirection}
            onChange={(e) => setSelectedDirection(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
          >
            <option value="">All Directions</option>
            <option value="to-church">To Church</option>
            <option value="from-church">From Church</option>
          </select>
        </div>
        <div>
          <label htmlFor="city" className="block text-sm font-medium text-gray-700">
            City
          </label>
          <select
            id="city"
            value={selectedCity}
            onChange={(e) => setSelectedCity(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
          >
            <option value="">All Cities</option>
            {getUniqueCities().map(city => (
              <option key={city} value={city}>{city}</option>
            ))}
          </select>
        </div>
      </div>

      {filteredRequests.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <FunnelIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <p className="text-gray-500">
            {requests.length === 0 ? 'No pending ride requests found.' : 'No ride requests match your filters.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filteredRequests.map((request) => {
            const direction = determineRideDirection(request);
            const isToChurch = direction === 'to-church';
            
            return (
              <div key={request.id} className="bg-white rounded-lg shadow overflow-hidden">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-2">
                      <CalendarIcon className="h-5 w-5 text-indigo-500" />
                      <span className="text-sm font-medium">{formatDate(request.date)}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      {/* Direction Indicator */}
                      <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${
                        isToChurch 
                          ? 'bg-indigo-100 text-indigo-800' 
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {isToChurch ? (
                          <>
                            <BuildingLibraryIcon className="h-3 w-3 mr-1" />
                            To Church
                          </>
                        ) : (
                          <>
                            <HomeIcon className="h-3 w-3 mr-1" />
                            From Church
                          </>
                        )}
                      </div>
                      {/* Status Badge */}
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                        request.status === 'pending' 
                          ? 'bg-yellow-100 text-yellow-800'
                          : request.status === 'accepted'
                          ? 'bg-green-100 text-green-800'
                          : request.status === 'declined'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {request.status}
                      </span>
                    </div>
                  </div>

                  {/* Basic Request Information */}
                  <div className="space-y-3">
                    {request.rider && (
                      <div className="flex items-center text-sm">
                        <UserIcon className="h-5 w-5 text-gray-400 mr-2" />
                        <span>
                          {request.rider.firstName}
                        </span>
                      </div>
                    )}
                    
                    <div className="flex items-start space-x-2">
                      <MapPinIcon className="h-5 w-5 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-xs text-gray-500">Pickup Location</p>
                        <p className="text-sm">
                          {request.pickupCity && request.pickupPostcode
                            ? (
                                <span className="text-gray-600">{request.pickupCity} {request.pickupPostcode}</span>
                              )
                            : (request.pickupLocation || 'N/A')
                          }
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-2">
                      <MapPinIcon className="h-5 w-5 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-xs text-gray-500">Destination</p>
                        <p className="text-sm">{request.destination || 'Church'}</p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      <UserGroupIcon className="h-5 w-5 text-gray-400" />
                      <div>
                        <p className="text-xs text-gray-500">Passengers</p>
                        <p className="text-sm">{request.seats || request.passengers || 1}</p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      <ClockIcon className="h-5 w-5 text-gray-400" />
                      <div>
                        <p className="text-xs text-gray-500">Service</p>
                        <p className="text-sm">{request.serviceSession} Service</p>
                      </div>
                    </div>

                    {/* Special Requests */}
                    {request.specialRequests && (
                      <div className="flex items-start space-x-2">
                        <ChatBubbleLeftRightIcon className="h-5 w-5 text-gray-400 mt-0.5" />
                        <div>
                          <p className="text-xs text-gray-500">Special Requests</p>
                          <p className="text-sm bg-gray-50 p-2 rounded text-gray-700">
                            {request.specialRequests}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* More Details Button */}
                  {/* Removed Show More / Show Less button and expanded details section as requested */}

                  {/* Actions */}
                  {request.status === 'pending' && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleAcceptRequest(request.id)}
                          className="flex-1 inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
                        >
                          <CheckIcon className="h-4 w-4 mr-1" />
                          Accept
                        </button>
                        <button
                          onClick={() => handleDeclineRequest(request.id)}
                          className="flex-1 inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700"
                        >
                          <XMarkIcon className="h-4 w-4 mr-1" />
                          Decline
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default RideRequests; 